import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import {
  createIntercompanyPair,
  updateIntercompanyPair,
} from "@/api/intercompany";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SELECT_CLASSES } from "@/lib/styles";
import type { IntercompanyPairView } from "@/types/api";

function isValidInn(v: string): boolean {
  return /^\d{10}$|^\d{12}$/.test(v.trim());
}

interface Props {
  pair: IntercompanyPairView | null; // null → создание
  onClose: () => void;
}

export function PairFormDialog({ pair, onClose }: Props): React.ReactElement {
  const qc = useQueryClient();
  const { connections } = useConnection();

  const [name, setName] = useState(pair?.name ?? "");
  const [senderInn, setSenderInn] = useState(pair?.sender_inn ?? "");
  const [receiverInn, setReceiverInn] = useState(pair?.receiver_inn ?? "");
  const [unfId, setUnfId] = useState(pair?.unf_connection_id ?? "");
  const [senderBpId, setSenderBpId] = useState(pair?.sender_bp_connection_id ?? "");
  const [receiverBpId, setReceiverBpId] = useState(pair?.receiver_bp_connection_id ?? "");

  const mut = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        sender_inn: senderInn.trim(),
        receiver_inn: receiverInn.trim(),
        unf_connection_id: unfId,
        sender_bp_connection_id: senderBpId,
        receiver_bp_connection_id: receiverBpId,
      };
      return pair ? updateIntercompanyPair(pair.id, body) : createIntercompanyPair(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["intercompany-pairs"] });
      toast.success(pair ? "Пара сохранена" : "Пара создана");
      onClose();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ??
        String(err);
      toast.error("Не удалось сохранить пару", { description: String(detail) });
    },
  });

  const canSubmit =
    name.trim().length > 0 &&
    isValidInn(senderInn) &&
    isValidInn(receiverInn) &&
    !!unfId &&
    !!senderBpId &&
    !!receiverBpId &&
    !mut.isPending;

  const connOptions = (
    <>
      <option value="">— выберите подключение —</option>
      {connections.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name} ({c.config_type}) — {c.odata_url}
        </option>
      ))}
    </>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{pair ? "Изменить пару организаций" : "Новая пара организаций"}</DialogTitle>
          <DialogDescription>
            Передатчик → получатель, ИНН + три подключения к 1С (УНФ, БП передатчика,
            БП получателя). Организации и контрагенты резолвятся по ИНН в рантайме.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="name">Название</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Омега → Еграшкин"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sender_inn">ИНН передатчика</Label>
              <Input
                id="sender_inn"
                value={senderInn}
                onChange={(e) => setSenderInn(e.target.value)}
                placeholder="10 или 12 цифр"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="receiver_inn">ИНН получателя</Label>
              <Input
                id="receiver_inn"
                value={receiverInn}
                onChange={(e) => setReceiverInn(e.target.value)}
                placeholder="10 или 12 цифр"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="unf">Подключение УНФ</Label>
            <select id="unf" className={SELECT_CLASSES} value={unfId} onChange={(e) => setUnfId(e.target.value)}>
              {connOptions}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender_bp">Подключение БП передатчика</Label>
            <select id="sender_bp" className={SELECT_CLASSES} value={senderBpId} onChange={(e) => setSenderBpId(e.target.value)}>
              {connOptions}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="receiver_bp">Подключение БП получателя</Label>
            <select id="receiver_bp" className={SELECT_CLASSES} value={receiverBpId} onChange={(e) => setReceiverBpId(e.target.value)}>
              {connOptions}
            </select>
          </div>
          {connections.length === 0 && (
            <p className="text-xs text-rose-600">
              У вас нет подключений к 1С — добавьте их в профиле.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button disabled={!canSubmit} onClick={() => mut.mutate()}>
            {mut.isPending ? "Сохранение…" : pair ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
